import AdWorkflowService from '@/entities/ad-workflow/ad-workflow.service';
import FlowableService from '@/entities/flowable/flowable.service';
import propertiesPanelModule from 'bpmn-js-properties-panel';
import 'bpmn-js-properties-panel/dist/assets/bpmn-js-properties-panel.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import Modeler from "bpmn-js/lib/Modeler";
import { saveAs } from "file-saver";
import JSZip from 'jszip';
import { Component, Inject, Vue } from "vue-property-decorator";
import {flowableModdleDescriptor} from './flowable-moddle-descriptor';
import propertiesProviderModule from './provider/flowable';
import stdTemplate from './stdTemplate.bpmn';

const WorkflowBuilderProps = Vue.extend({
  props: {
    data: Object
  }
});

@Component
export default class WorkflowBuilder extends WorkflowBuilderProps {

  @Inject('adWorkflowService')
  private adWorkflowService: () => AdWorkflowService;

  @Inject('flowableService')
  private flowableService: () => FlowableService;

  private modeler: Modeler = null;
  private eventBus: any;
  private fileReader = new FileReader();

  loading: boolean = false;
  selectedElement: any = {};

  onClosed(refreshFn: Function) {
    refreshFn();
  }

  onOpened() {
    this.createOrRetrieve();
  }

  onUploadChange() {
    const file: File = (this.$refs.inputFile as HTMLInputElement).files.item(0);
    this.fileReader.readAsText(file);
  }

  onElementChanged(e) {
    this.selectedElement = e.element;
  }

  onElementClicked(e) {
    this.selectedElement = e.element;
  }

  created() {
    this.fileReader.onload = (e) => {
      const xml = e.target.result;
      this.openDiagram(xml);
    };
  }

  mounted() {
    this.modeler = new Modeler({
      container: this.$refs.canvas,
      propertiesPanel: {
        parent: (this.$refs.propertiesPanel as Vue).$el.querySelector('.el-scrollbar__view')
      },
      additionalModules: [
        propertiesPanelModule,
        propertiesProviderModule
      ],
      moddleExtensions: {
        flowable: flowableModdleDescriptor
      }
    });

    this.eventBus = this.modeler.get('eventBus');
    this.eventBus.on('element.changed', e => this.onElementChanged(e));
    this.eventBus.on('element.click', e => this.onElementClicked(e));
  }

  beforeDestroy() {
    this.eventBus.off('element.changed', e => this.onElementChanged(e));
    this.eventBus.off('element.click', e => this.onElementClicked(e));
    this.modeler.destroy();
  }

  createOrRetrieve() {
    if (this.data.deploymentId) {
      this.retrieveDiagram();
    } else {
      this.openDiagram(stdTemplate);
    }
  }

  newDiagram() {
    this.$confirm('Do you want to discard the diagram?')
      .then(() => {
        this.openDiagram(stdTemplate);
      })
      .catch(() => {/* No action needed if cancel */});
  }

  openDiagram(xml) {
    this.modeler.importXML(xml)
      .then(_result => {
        this.modeler.get('canvas').zoom('fit-viewport');
      }).catch(function(err) {
        const { warnings, message } = err;
        console.error('Failed to import the diagram:', warnings, message);
      });
  }

  openLocalFile() {
    (this.$refs.inputFile as HTMLInputElement).click();
  }

  retrieveDiagram() {
    this.loading = true;
    this.flowableService()
      .getDeploymentResourceData(this.data.deploymentId, this.data.code + '.bpmn20.xml')
      .then(xml => this.openDiagram(xml))
      .catch(err => this.$message.error('Failed getting the diagram'))
      .finally(() => this.loading = false)
  }

  saveDiagram() {
    this.loading = true;
    this.modeler.saveXML()
      .then(bpmn => {
        const formData = new FormData();
        const blob = new File([bpmn.xml], `${this.data.code}.bpmn20.xml`, { type: 'application/xml' });
        const zip = new JSZip();

        zip.file(`${this.data.code}.bpmn20.xml`, blob);
        zip.generateAsync({ type: 'blob' })
          .then(file => {
            formData.append('file', file);
            formData.append('workflowId', String(this.data.id));
            this.adWorkflowService().saveDiagram(formData)
              .then(_res => this.$message.success('Process definition has been successfully saved'))
              .catch(err => console.error('Failed to save diagram.', err));
          });
      })
      .finally(() => this.loading = false);
    
  }

  saveAsBPMN() {
    this.modeler.saveXML()
      .then(bpmn => {
        const blob = new File([bpmn.xml], `${this.data.code}.bpmn20.xml`, { type: 'application/xml' });
        const zip = new JSZip();

        zip.file(`${this.data.code}.bpmn20.xml`, blob);
        zip.generateAsync({ type: 'blob' })
          .then(file => {
            saveAs(file, `${this.data.name}.zip`);
          });
      });
  }

  saveAsSVG() {
    this.modeler.saveSVG()
      .then(bpmn => {
        const file = new Blob([bpmn.svg], {type: 'image/svg+xml'});
        saveAs(file, `${this.data.name}.svg`);
      });
  }
}